import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import { findProduct } from '../bz/products';
import '../bsa/projects/projectsList.css';
import { BugLink, SETTINGS_PATH, ScoreChip } from './PredictWatchlist';
import { PROMOTION_SCORE, formatDate, formatDateTime, formatScore, isPromoted } from './predictWatchlistModel';
import {
  CLASSES,
  classCounts,
  classLabel,
  cveUrl,
  filterByClass,
  formatLead,
  outcomeKey,
  visibleOutcomes
} from './predictOutcomesModel';

const small = { fontSize: 'var(--fs-sm)' };

const PILL_TONE = { predicted: 'bz-pill--predict', 'retro-matched': 'bz-pill--info' };

const ClassPill = ({ cls }) => <span className={`bz-pill ${PILL_TONE[cls]}`}>{classLabel(cls)}</span>;
ClassPill.propTypes = { cls: PropTypes.oneOf(CLASSES).isRequired };

// A class chip carries its own count only; "All" has none, because the counting
// rule never adds Prediction and Detection together.
const ClassChip = ({ on, onClick, label, count }) => (
  <button
    type="button"
    className={`bz-btn bz-btn--sm${on ? ' bz-btn--primary' : ''}`}
    aria-pressed={on}
    aria-label={count === undefined ? label : `${label}, ${count} ${count === 1 ? 'conversion' : 'conversions'}`}
    onClick={onClick}
  >
    {label}
    {count !== undefined && <span style={{ opacity: 0.7, marginLeft: 6 }}>{count}</span>}
  </button>
);
ClassChip.propTypes = {
  on: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number
};

const LEGEND = [
  {
    key: 'predicted',
    title: <ClassPill cls="predicted" />,
    text: "The bug wasn't filed as a security issue, and it later became a CVE."
  },
  {
    key: 'retro-matched',
    title: <ClassPill cls="retro-matched" />,
    text: 'The bug was filed as a security issue, or described an attack, and it later became a CVE.'
  },
  {
    key: 'lead',
    title: <b style={small}>Lead</b>,
    text:
      "Days from the freeze, when Predict kept the bug's first score, to the first public statement of the flaw, such as the CVE's publication or a fix whose title states it."
  }
];

const Cve = ({ cve }) => {
  const href = cveUrl(cve);
  const id = <span className="bz-mono">{cve}</span>;
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
      {id}
    </a>
  ) : (
    id
  );
};
Cve.propTypes = { cve: PropTypes.string.isRequired };

/**
 * Predict → Outcomes (STRY0038978): the promoted bugs that became CVEs.
 *
 * The conversions in the customer's Predict scope that score at or above their
 * threshold, from GET /predict/outcomes. The API decides the scope and the
 * threshold, as for the watch list, so the page sends neither. Each conversion
 * is labelled Prediction (predicted) or Detection (retro-matched); no other
 * class reaches the page. Eric's MVP rule: the page shows conversions only,
 * with no rate, percentage or total against all promoted bugs, and says that
 * conversions are a floor.
 *
 * Real data only; the route sits behind <ProductGuard product="predict">.
 */
const PredictOutcomes = () => {
  const [list, setList] = useState({ status: 'loading', outcomes: [], threshold: null, asOf: null });
  const [shownClass, setShownClass] = useState(null);
  const loadId = useRef(0);

  // A Refresh started since wins.
  const load = useCallback(() => {
    const id = ++loadId.current;
    setList(state => ({ ...state, status: 'loading' }));
    BZAPIService.getPredictOutcomes()
      .then(body => {
        if (id !== loadId.current) return;
        setList({
          status: 'ready',
          outcomes: visibleOutcomes(body?.outcomes),
          threshold: typeof body?.threshold === 'number' ? body.threshold : null,
          asOf: body?.asOf || null
        });
      })
      .catch(error => {
        console.error('Failed to load the Predict outcomes:', error);
        if (id === loadId.current) setList({ status: 'failed', outcomes: [], threshold: null, asOf: null });
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => classCounts(list.outcomes), [list.outcomes]);
  const rows = useMemo(() => filterByClass(list.outcomes, shownClass), [list.outcomes, shownClass]);
  const isLoading = list.status === 'loading';

  return (
    <div
      style={{
        padding: '24px 32px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 20
      }}
    >
      <div className="bz-section-head" style={{ marginBottom: 0 }}>
        <div>
          <div className="bz-kicker" style={{ color: findProduct('predict').accent }}>
            Predict · Outcomes
          </div>
          <h1 className="bz-h1" style={{ marginTop: 4 }}>
            Outcomes
          </h1>
          <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
            Bugs in your scope that Predict scored, and that later became CVEs.
          </div>
        </div>
        <button type="button" className="bz-btn bz-btn--sm" onClick={load} disabled={isLoading}>
          <FontAwesomeIcon icon={faRefresh} className={isLoading ? 'fa-spin' : ''} />
          {' Refresh'}
        </button>
      </div>

      <div
        role="note"
        className="bz-row"
        style={{
          gap: 12,
          flexWrap: 'wrap',
          padding: '12px 14px',
          borderRadius: 'var(--r-md)',
          background: 'var(--info-soft)',
          fontSize: 'var(--fs-md)'
        }}
      >
        <p style={{ margin: 0, flex: '1 1 320px' }}>
          <b>Conversions are a floor.</b> Predict only finds a CVE that names the bug, so some conversions are missing
          from this list. It shows bugs at or above your threshold (
          <span className="bz-mono" data-testid="threshold">
            {formatScore(list.threshold)}
          </span>
          ).
        </p>
        <Link to={SETTINGS_PATH} className="bz-btn bz-btn--ghost bz-btn--sm">
          Settings
        </Link>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 12
        }}
      >
        {LEGEND.map(item => (
          <div
            key={item.key}
            style={{
              padding: '12px 14px',
              border: '1px solid var(--line-soft)',
              borderRadius: 'var(--r-md)',
              background: 'var(--bg-soft)'
            }}
          >
            {item.title}
            <p className="bz-soft" style={{ ...small, margin: '6px 0 0' }}>
              {item.text}
            </p>
          </div>
        ))}
      </div>

      <div className="bz-card">
        <div
          className="bz-row"
          style={{ gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--line-soft)' }}
        >
          <h2 className="bz-h3">Conversions in your scope</h2>
          <div className="bz-grow" />
          {list.status === 'ready' && list.outcomes.length > 0 && (
            <div role="group" aria-label="Class" className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <ClassChip on={!shownClass} label="All" onClick={() => setShownClass(null)} />
              {CLASSES.map(cls => (
                <ClassChip
                  key={cls}
                  on={shownClass === cls}
                  label={classLabel(cls)}
                  count={counts[cls]}
                  onClick={() => setShownClass(current => (current === cls ? null : cls))}
                />
              ))}
            </div>
          )}
        </div>

        {list.status === 'loading' && list.outcomes.length === 0 ? (
          <div className="text-center" style={{ padding: 40 }}>
            <Spinner color="primary" />
            <div className="bz-muted" style={{ marginTop: 8 }}>
              Loading the outcomes…
            </div>
          </div>
        ) : list.status === 'failed' ? (
          <div style={{ padding: 16 }}>
            <div className="pl-empty">
              <div className="pl-empty__title">Predict data is unavailable right now.</div>
              <div className="bz-muted">Try again in a minute.</div>
              <button type="button" className="bz-btn bz-btn--sm" onClick={load}>
                Try again
              </button>
            </div>
          </div>
        ) : list.outcomes.length === 0 ? (
          <div style={{ padding: 16 }}>
            <div className="pl-empty">
              <div className="pl-empty__title">No conversions yet.</div>
              <div className="bz-muted">
                Predict hasn&apos;t found a CVE for any of your bugs at or above {formatScore(list.threshold)} yet.
              </div>
              <Link to="/predict/watch" className="bz-btn bz-btn--sm">
                See the watch list
              </Link>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 16 }}>
            <div className="pl-empty">
              <div className="pl-empty__title">No {classLabel(shownClass)} conversions yet.</div>
              <button type="button" className="bz-btn bz-btn--sm" onClick={() => setShownClass(null)}>
                Show all
              </button>
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="bz-table" style={{ minWidth: 860 }}>
              <thead>
                <tr>
                  <th>Bug</th>
                  <th>Score</th>
                  <th>Frozen</th>
                  <th>CVE</th>
                  <th>First public statement</th>
                  <th>Lead</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(outcome => (
                  <tr key={outcomeKey(outcome)} data-testid="outcome-row">
                    <td style={{ minWidth: 280, maxWidth: 520 }}>
                      <div style={{ fontWeight: 500 }}>
                        <BugLink url={outcome.url}>{outcome.title || outcome.bugId}</BugLink>
                      </div>
                      <div className="bz-muted" style={{ ...small, marginTop: 2 }}>
                        {outcome.targetName || outcome.target} · <span className="bz-mono">{outcome.bugId}</span>
                      </div>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <ScoreChip score={outcome.score} />
                      {!isPromoted(outcome) && (
                        <span
                          className="bz-muted"
                          style={{ display: 'block', fontSize: 10, marginTop: 3 }}
                          title={`Under ${formatScore(PROMOTION_SCORE)}. Shown because your threshold is ${formatScore(
                            list.threshold
                          )}.`}
                        >
                          Not promoted
                        </span>
                      )}
                    </td>
                    <td className="bz-mono" style={{ ...small, whiteSpace: 'nowrap' }}>
                      {formatDate(outcome.frozenAt)}
                    </td>
                    <td style={{ ...small, whiteSpace: 'nowrap' }}>
                      <Cve cve={outcome.cve} />
                    </td>
                    <td style={{ ...small, whiteSpace: 'nowrap' }}>{formatDate(outcome.firstPublicAt)}</td>
                    <td className="bz-mono" style={{ ...small, whiteSpace: 'nowrap', fontWeight: 600 }}>
                      {formatLead(outcome.leadDays)}
                    </td>
                    <td>
                      <ClassPill cls={outcome.class} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div
          className="bz-muted bz-row"
          style={{ ...small, gap: 12, flexWrap: 'wrap', padding: '12px 16px', borderTop: '1px solid var(--line-soft)' }}
        >
          <span>Not listed: coordinated, placeholder, plausible-only and unreviewed conversions.</span>
          <div className="bz-grow" />
          {list.asOf && <span data-testid="as-of">Updated {formatDateTime(list.asOf)}</span>}
        </div>
      </div>
    </div>
  );
};

export default PredictOutcomes;
